import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from '../lib/toast';
import { translateBackendError } from '../utils/errorTranslator';
import LanguageSwitcher from '../components/LanguageSwitcher';
import GoogleSignInButton from '../components/GoogleSignInButton';
import { useLanguage } from '../hooks/useLanguage';
import { detectLanguageFromMarketing } from '../utils/languageDetection';
import { LEGAL_URLS } from '../lib/legalUrls';
import './LoginPage.css';

function LoginPage() {
  const { t, i18n } = useTranslation(['auth', 'common']);
  const { changeLanguage, currentLanguage } = useLanguage();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // Shown only when user tried "Sign in with Google" without an account and must accept terms to create one
  const [showTermsCheckbox, setShowTermsCheckbox] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const { login, googleLogin } = useAuth();
  const navigate = useNavigate();

  // Detect and set language from marketing site on mount
  // Only apply marketing language if user has NO saved language preference yet
  useEffect(() => {
    const hasExistingPreference = localStorage.getItem('preferred_language');
    if (!hasExistingPreference) {
      const detectedLang = detectLanguageFromMarketing();
      if (detectedLang) {
        changeLanguage(detectedLang).catch(() => {
          // Silently fail if language change fails
        });
      }
    }
  }, [changeLanguage]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // console.log('🎬 LoginPage.handleSubmit called');
    // console.log('📝 Form data:', { email: formData.email, password: '***' });

    setError('');
    setLoading(true);

    try {
      // console.log('📞 Calling login from AuthContext...');
      const result = await login(formData.email, formData.password);
      // console.log('📞 Login result:', result);

      if (result.success) {
        // console.log('✅ Login successful, navigating to dashboard...');
        navigate('/dashboard');
      } else {
        // console.log('❌ Login failed, showing error:', result.error);
        const translatedError = translateBackendError(result.error || '');
        setError(translatedError);
        toast.raw.error(translatedError);
      }
    } catch (err) {
      // console.log('💥 LoginPage error:', err);
      const errorMessage = 'An unexpected error occurred';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      // console.log('🏁 LoginPage handleSubmit finished');
      setLoading(false);
    }
  };

  const handleGoogleSuccess = useCallback(async (credential) => {
    setError('');
    setLoading(true);
    try {
      const currentLang = currentLanguage || i18n.language?.substring(0, 2) || null;
      const result = await googleLogin(credential, currentLang, termsAccepted);
      if (result.success) {
        navigate('/dashboard');
      } else {
        const translatedError = translateBackendError(result.error || '');
        setError(translatedError);
        toast.raw.error(translatedError);
        // If backend says they must accept terms to create an account, show the checkbox so they can retry
        if (result.error && /accept.*terms|terms.*accept/i.test(result.error)) {
          setShowTermsCheckbox(true);
        }
      }
    } catch (err) {
      const errorMessage = t('auth:google.error', 'Google sign-in failed. Please try again.');
      setError(errorMessage);
      toast.raw.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [googleLogin, navigate, t, currentLanguage, i18n.language, termsAccepted]);

  const handleGoogleError = useCallback((error) => {
    const errorMessage = t('auth:google.error', 'Google sign-in failed. Please try again.');
    setError(errorMessage);
    toast.raw.error(errorMessage);
  }, [t]);

  return (
    <div className="login-container">

      {/* ── Left: image panel ── */}
      <div className="login-image-panel">
        <div className="login-image-overlay">
          <div className="brand-name">BizContent.ai</div>
          <div className="brand-tagline">{t('common:tagline')}</div>
        </div>
      </div>

      {/* ── Right: form panel ── */}
      <div className="login-form-panel">
      <div className="login-card">
        <div className="login-header">
          <h1>BizContent.ai</h1>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="email">{t('auth:login.email')}</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder={t('auth:login.emailPlaceholder')}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">{t('auth:login.password')}</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              placeholder={t('auth:login.passwordPlaceholder')}
            />
            <div className="forgot-password-link">
              <Link to="/forgot-password">
                {t('auth:login.forgotPassword')}
              </Link>
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}

          {showTermsCheckbox && (
            <div className="form-group" style={{ flexDirection: 'row', alignItems: 'flex-start', gap: '0.5rem', marginTop: '0.5rem', fontSize: '0.8rem' }}>
              <input
                type="checkbox"
                id="terms-accept-login"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                style={{ marginTop: '0.25rem' }}
              />
              <label htmlFor="terms-accept-login" style={{ flex: 1, cursor: 'pointer', fontSize: '0.8rem' }}>
                <Trans
                  i18nKey="auth:terms.acceptLabel"
                  components={[
                    <a href={LEGAL_URLS.terms} target="_blank" rel="noopener noreferrer" key="terms" style={{ color: '#8b9cff', textDecoration: 'underline', fontSize: '0.8rem' }} />,
                    <a href={LEGAL_URLS.privacy} target="_blank" rel="noopener noreferrer" key="privacy" style={{ color: '#8b9cff', textDecoration: 'underline', fontSize: '0.8rem' }} />,
                    <a href={LEGAL_URLS.cookies} target="_blank" rel="noopener noreferrer" key="cookies" style={{ color: '#8b9cff', textDecoration: 'underline', fontSize: '0.8rem' }} />,
                  ]}
                />
              </label>
            </div>
          )}

          <button
            type="submit"
            className="login-button"
            disabled={loading}
          >
            {loading ? t('auth:login.submitting') : t('auth:login.submit')}
          </button>
        </form>

        <div className="auth-divider">
          <span>{t('auth:google.or', 'or')}</span>
        </div>

        <div className="google-signin-container">
          <GoogleSignInButton
            onSuccess={handleGoogleSuccess}
            onError={handleGoogleError}
            buttonText="signin_with"
            disabled={loading || (showTermsCheckbox && !termsAccepted)}
          />
        </div>

        <div className="login-footer">
          <div className="register-link">
            <p>{t('auth:login.noAccount')} <Link to="/register" className="register-link-text">{t('auth:login.createAccount')}</Link></p>
          </div>
          <div className="language-switcher-container" style={{ marginTop: '1rem', display: 'flex', justifyContent: 'center' }}>
            <LanguageSwitcher variant="compact" showLabel={false} />
          </div>
        </div>
      </div>
      </div>{/* end login-form-panel */}
    </div>
  );
}

export default LoginPage;
